import { TokenizedResponseInvalidError } from './errors';

export const apiDataKey = '_apiResponse';

export type TSchemaValueType =
  | 'id'
  | 'reference'
  | 'string'
  | 'number'
  | 'boolean'
  | 'email'
  | 'handle'
  | 'phoneNumber'
  | 'rfc3339Timestamp'
  | 'nanosecondTimestamp'
  | 'object'
  | 'array'
  | 'dictionary'
  | 'selectType'
  | 'enumerated'
  | 'enumeratedNumber'
  | 'JSON'
  | 'modelEntity';

type EntityMapsByType = Record<string, Map<string, unknown>>;

export type SchemaValueRecord = Record<string, SchemaValue<unknown>>;

type ValidateFunction<OutputType> = (
  value: unknown,
  path: string,
  entities: EntityMapsByType,
  parent: Record<string, unknown> | null,
) => OutputType;

/** @internal */
export class SchemaValue<OutputType> {
  type: TSchemaValueType;
  validateFunction: ValidateFunction<OutputType>;
  isRequired: boolean;
  treatEmptyStringAsNull: boolean;
  checkMissingValue: boolean;
  outputKey?: string;
  referenceType?: string;
  isId: boolean;

  constructor(
    type: TSchemaValueType,
    validateFunction: ValidateFunction<OutputType>,
    { checkMissingValue = true, treatEmptyStringAsNull = true } = {},
  ) {
    this.type = type;
    this.validateFunction = validateFunction;
    this.isRequired = false;
    this.checkMissingValue = !!checkMissingValue;
    this.treatEmptyStringAsNull = !!treatEmptyStringAsNull;
    this.isId = false;
  }

  required() {
    this.isRequired = true;
    return this;
  }

  as(newKey: string) {
    this.outputKey = newKey;
    return this;
  }

  validate(
    value: unknown,
    path: string,
    entities: EntityMapsByType,
    parent: Record<string, unknown> | null,
  ): OutputType | null | undefined {
    if (this.treatEmptyStringAsNull && value === '') {
      value = null;
    }
    if (this.checkMissingValue && (value === undefined || value === null)) {
      if (this.isRequired) {
        throw new TokenizedResponseInvalidError(
          `${path} has no value (required)`,
          { type: this.type, path, valueType: typeof value },
        );
      }
      return value;
    } else {
      return this.validateFunction(value, path, entities, parent);
    }
  }
}

export type OutputTypeFromSchemaValue<V> = V extends SchemaValue<
  infer OutputType
>
  ? OutputType
  : never;

export type OutputTypeWithApiData<OutputType extends Record<string, unknown>> =
  OutputType & { [apiDataKey]: unknown };

/** @internal */
export class IdSchemaValue extends SchemaValue<string> {
  constructor(
    referenceType: string | undefined,
    validateFunction: ValidateFunction<string>,
  ) {
    super('id', validateFunction);
    this.referenceType = referenceType;
    this.isId = true;
  }
}

/** @internal */
export class ReferenceSchemaValue extends SchemaValue<string> {
  constructor(
    referenceType: string,
    validateFunction: ValidateFunction<string>,
  ) {
    super('reference', validateFunction);
    this.referenceType = referenceType;
  }
}

/** @internal */
export class ContainerSchemaValue<OutputType> extends SchemaValue<OutputType> {
  containedObjectSchema: SchemaValueRecord | null;

  constructor(
    containedObjectSchema: SchemaValueRecord | null,
    type: TSchemaValueType,
    validateFunction: ValidateFunction<OutputType>,
  ) {
    super(type, validateFunction, { treatEmptyStringAsNull: false });
    this.containedObjectSchema = containedObjectSchema;
  }
}

type TypeKeySelector = string | ((parent: Record<string, unknown>) => string);

/** @internal */
export class SelectTypeSchemaValue<OutputType> extends SchemaValue<OutputType> {
  typeKey: TypeKeySelector;
  schemaMap: SchemaValueRecord;

  constructor(
    typeKey: TypeKeySelector,
    schemaMap: SchemaValueRecord,
    validateFunction: ValidateFunction<OutputType>,
  ) {
    super('selectType', validateFunction, { treatEmptyStringAsNull: false });
    this.typeKey = typeKey;
    this.schemaMap = schemaMap;
  }
}

export function id(referenceType?: string) {
  return new IdSchemaValue(referenceType, (value, path) => {
    if (typeof value !== 'string') {
      throw new TokenizedResponseInvalidError(
        `${path} is ${typeof value}, expected string (id)`,
        { type: 'id', path, valueType: typeof value },
      );
    }
    return value;
  });
}

export function reference(referenceType: string) {
  return new ReferenceSchemaValue(referenceType, (value, path) => {
    if (typeof value !== 'string') {
      throw new TokenizedResponseInvalidError(
        `${path} is ${typeof value}, expected string (reference)`,
        { type: 'reference', referenceType, path, valueType: typeof value },
      );
    }
    return value;
  });
}

export function string() {
  return new SchemaValue<string>('string', (value, path) => {
    if (typeof value !== 'string') {
      throw new TokenizedResponseInvalidError(
        `${path} is ${typeof value}, expected string`,
        { type: 'string', path, valueType: typeof value },
      );
    }
    return value;
  });
}

export function number() {
  return new SchemaValue<number>(
    'number',
    (value, path) => {
      if (typeof value !== 'number') {
        throw new TokenizedResponseInvalidError(
          `${path} is ${typeof value}, expected number`,
          { type: 'number', path, valueType: typeof value },
        );
      }
      return value;
    },
    { treatEmptyStringAsNull: false },
  );
}

export function boolean() {
  return new SchemaValue<boolean>(
    'boolean',
    (value, path) => {
      if (value && typeof value !== 'boolean') {
        throw new TokenizedResponseInvalidError(
          `${path} is ${typeof value}, expected boolean`,
          { type: 'boolean', path, valueType: typeof value },
        );
      }
      // Convert all falsey values to false
      return !!value;
    },
    { checkMissingValue: false, treatEmptyStringAsNull: false },
  );
}

export function rfc3339Timestamp() {
  return new SchemaValue<number>('rfc3339Timestamp', (value, path) => {
    if (typeof value !== 'string') {
      throw new TokenizedResponseInvalidError(
        `${path} is ${typeof value}, expected string (rfc3339Timestamp)`,
        { type: 'rfc3339Timestamp', path, valueType: typeof value },
      );
    }
    const timestamp = Date.parse(value);
    if (isNaN(timestamp)) {
      throw new TokenizedResponseInvalidError(
        `${path} not a valid RFC 3339 timestamp`,
        { type: 'rfc3339Timestamp', path, valueType: typeof value },
      );
    }
    return timestamp;
  });
}

export function nanosecondTimestamp() {
  return new SchemaValue<number>(
    'nanosecondTimestamp',
    (value, path) => {
      if (typeof value !== 'number') {
        throw new TokenizedResponseInvalidError(
          `${path} is ${typeof value}, expected number (nanosecondTimestamp)`,
          { type: 'nanosecondTimestamp', path, valueType: typeof value },
        );
      }
      return value / 1e6;
    },
    { treatEmptyStringAsNull: false },
  );
}

export function object<OutputType = unknown>(
  objectSchema: SchemaValueRecord | ContainerSchemaValue<OutputType>,
) {
  if (objectSchema instanceof ContainerSchemaValue) {
    if (objectSchema.type !== 'object' || !objectSchema.containedObjectSchema) {
      throw new Error(
        `Incompatible schema of type ${objectSchema.type} passed to Schema.object, expecting 'object'`,
      );
    }
    // Support an object value that references a
    // separate object schema
    objectSchema = objectSchema.containedObjectSchema;
  }
  return new ContainerSchemaValue<OutputType>(
    objectSchema,
    'object',
    (value, path, entities) => {
      if (!value || typeof value !== 'object') {
        throw new TokenizedResponseInvalidError(
          `${path} is ${typeof value}, expected object`,
          { type: 'object', path, valueType: typeof value },
        );
      }
      const object = { [apiDataKey]: value };
      Object.entries(objectSchema).forEach(([schemaKey, schemaValue]) =>
        normalizeValue(
          (value as Record<string, unknown>)[schemaKey],
          schemaValue,
          object,
          schemaKey,
          `${path}.${schemaKey}`,
          entities,
        ),
      );
      return object as unknown as OutputType;
    },
  );
}

export function arrayOf<OutputType>(itemSchema: SchemaValue<OutputType>) {
  return new ContainerSchemaValue<OutputType[]>(
    null,
    'array',
    (value, path, entities) => {
      if (!Array.isArray(value)) {
        throw new TokenizedResponseInvalidError(
          `${path} is ${typeof value}, expected array`,
          { type: 'array', path, valueType: typeof value },
        );
      }
      const array = value.map(
        (item, index) =>
          normalizeValue(
            item,
            itemSchema,
            null,
            null,
            `${path}[${index}]`,
            entities,
          ) as OutputType,
      );
      return array;
    },
  );
}

export function dictionary<OutputType>(itemSchema: SchemaValue<OutputType>) {
  return new ContainerSchemaValue<Map<string, OutputType>>(
    null,
    'dictionary',
    (value, path, entities) => {
      if (!value || typeof value !== 'object') {
        throw new TokenizedResponseInvalidError(
          `${path} is ${typeof value}, expected object`,
          { type: 'dictionary', path, valueType: typeof value },
        );
      }
      return new Map(
        Object.entries(value).map(([key, item]) => [
          key,
          normalizeValue(
            item,
            itemSchema,
            null,
            null,
            `${path}[${key}]`,
            entities,
          ) as OutputType,
        ]),
      );
    },
  );
}

export function selectTypeViaKey<
  M extends SchemaValueRecord,
  MK extends keyof M,
  D extends SchemaValue<unknown>,
>(typeKey: TypeKeySelector, schemaMap: M, defaultSchema?: D) {
  return new SelectTypeSchemaValue<
    OutputTypeFromSchemaValue<M[MK] | D> | undefined
  >(
    typeKey,
    schemaMap,
    (
      value,
      path,
      entities,
      parent,
    ): OutputTypeFromSchemaValue<M[MK] | D> | undefined => {
      const typeKeyValue =
        typeof typeKey === 'function'
          ? typeKey(parent || {})
          : parent?.[typeKey];
      const schema =
        (typeof typeKeyValue === 'string' && schemaMap?.[typeKeyValue]) ||
        defaultSchema;
      if (!schema) {
        return undefined;
      }
      return schema.validateFunction(
        value,
        path,
        entities,
        parent,
      ) as OutputTypeFromSchemaValue<M[MK] | D>;
    },
  );
}

export function enumerated(values: readonly string[]) {
  return new SchemaValue<string>('enumerated', (value, path) => {
    if (typeof value !== 'string') {
      throw new TokenizedResponseInvalidError(
        `${path} is ${typeof value}, expected string`,
        { type: 'enumerated', path, valueType: typeof value },
      );
    }
    if (value && !values.includes(value)) {
      console.error(`${path} is ${value}, expected one of ${values}`);
    }
    return value;
  });
}

export function enumeratedNumber(values: number[]) {
  return new SchemaValue<number>('enumeratedNumber', (value, path) => {
    if (typeof value !== 'number') {
      throw new TokenizedResponseInvalidError(
        `${path} is ${typeof value}, expected number`,
        { type: 'enumeratedNumber', path, valueType: typeof value },
      );
    }
    if (value && !values.includes(value)) {
      console.error(`${path} is ${value}, expected one of ${values}`);
    }
    return value;
  });
}

export function embeddedJSON<Schema extends SchemaValue<unknown>>(
  embeddedSchema: Schema,
) {
  return new ContainerSchemaValue<OutputTypeFromSchemaValue<Schema>>(
    null,
    'JSON',
    (value, path, entities): OutputTypeFromSchemaValue<Schema> => {
      if (typeof value != 'string') {
        throw new TokenizedResponseInvalidError(
          `${path} is ${typeof value}, expected string`,
          { type: 'JSON', path, valueType: typeof value },
        );
      }
      return normalizeValue(
        JSON.parse(value),
        embeddedSchema,
        null,
        null,
        `JSON.parse(${path})`,
        entities,
      ) as OutputTypeFromSchemaValue<Schema>;
    },
  );
}

export function modelEntity<OutputType = unknown>(
  entityType: string,
  entitySchema: SchemaValueRecord,
  { idOptional = false } = {},
) {
  return new ContainerSchemaValue<OutputType>(
    entitySchema,
    'modelEntity',
    (value, path, entities) => {
      if (!value || typeof value !== 'object') {
        throw new TokenizedResponseInvalidError(
          `${path} is ${typeof value}, expected object (${entityType})`,
          { type: 'modelEntity', entityType, path, valueType: typeof value },
        );
      }
      const entity = { [apiDataKey]: value };
      let idValue: undefined | string;
      Object.entries(entitySchema).forEach(([schemaKey, schemaValue]) => {
        const entityValue = normalizeValue(
          (value as Record<string, unknown>)[schemaKey],
          schemaValue,
          entity,
          schemaKey,
          `${path}.${schemaKey}`,
          entities,
        );
        if (!idValue && entityValue && schemaValue?.isId) {
          idValue = entityValue as string;
        }
      });
      if (idValue) {
        if (!entities[entityType]) {
          entities[entityType] = new Map();
        }
        entities[entityType].set(idValue, {
          ...(entities[entityType].get(idValue) as OutputType),
          ...entity,
        });
      } else if (!idOptional) {
        throw new TokenizedResponseInvalidError(
          `${path} entity (${entityType}) has no id`,
          { type: 'modelEntity', entityType, path, valueType: typeof value },
        );
      }
      return entity as unknown as OutputType;
    },
  );
}

function toCamelCase(input: string) {
  return input
    .replace(/./, (text) => text.toLowerCase())
    .replaceAll(/_(.)/g, (_, initial) => initial.toUpperCase());
}

function normalizeValue<OutputType>(
  value: unknown,
  schemaValue: SchemaValue<OutputType>,
  parent: Record<string, unknown> | null,
  key: string | null,
  path: string,
  entities: EntityMapsByType,
) {
  const outputValue = schemaValue.validate(value, path, entities, parent);
  if (parent && key) {
    parent[schemaValue.outputKey || toCamelCase(key)] = outputValue;
  }
  return outputValue;
}

export function normalizeResponse<OutputType>(
  requestName: string,
  data: unknown,
  schema: SchemaValue<OutputType>,
): {
  normalizedValue: OutputType | null | undefined;
  modelEntities: EntityMapsByType;
} {
  const result = {
    normalizedValue: null,
    modelEntities: {},
  };
  try {
    normalizeValue(
      data,
      schema,
      result,
      'normalizedValue',
      'data',
      result.modelEntities,
    );
  } catch (error) {
    console.error(error);
    throw new TokenizedResponseInvalidError(
      requestName && `${requestName} received invalid data`,
      { fromError: error },
    );
  }
  return result;
}
